import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Button, Heading, Link } from '@primer/react';
import ComponentLayout from '~/src/layouts/component-layout';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Note = makeShortcode("Note");
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const CustomVideoPlayer = makeShortcode("CustomVideoPlayer");
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <Note mdxType="Note">
  Looking for <a href="/guides/contribute">contributors</a> to add submenus to the React and Rails implementation, as
  they are currently not available in this component.
    </Note>
    <h2>{`Usage`}</h2>
    <p>{`An action menu comprises a set of `}<a parentName="p" {...{
        "href": "/components/action-list"
      }}>{`action list`}</a>{` items, where each item represents an action, command, submenu, or the current selection that can be either a single or multi-select. The action menu can be invoked by clicking on a `}<a parentName="p" {...{
        "href": "/components/button"
      }}>{`button`}</a>{`, `}<a parentName="p" {...{
        "href": "/components/icon-button"
      }}>{`icon button`}</a>{`, or right-clicking on the list items.`}</p>
    <img width="960" alt="Three instances of action menus. The initial one comprises an icon button that provides two actions. The second one displays a button labeled View:Table and presents three choices (Table, Board, and Roadmap), with Table being the currently selected option. Finally, the third menu is a context menu that appears on right-click and contains a diverse set of options, including single select, submenus, and actions." src="https://user-images.githubusercontent.com/980622/233374605-cee72894-fc5e-46f4-a1ea-e82aa26084ef.png" />
    <p>{`The action menu is an extremely versatile component that is not restricted to a single type of action list items at a time. It can be used to create a combination of single select, multi-select, commands, and submenus.`}</p>
    <p>{`By using dividers and section titles, a hierarchy can be established within the action menu, making it easier to navigate and understand. This allows users to quickly find and access the actions they need, while also providing a clear and organized structure to the menu.`}</p>
    <img src="https://user-images.githubusercontent.com/980622/234302061-4a6c820f-5467-4648-b70e-04d16ff2b5a5.png" width="960" alt="Five examples of how an action menu can be utilized, which includes a context menu, multi-select, single-select, actions, and a combination of multi-select and single-select." />
    <p>{`As the action menu is a highly adaptable component, it's crucial to keep the interactions with individual items limited. The following interactions are recommended:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`enter`}</inlineCode>{`, `}<inlineCode parentName="li">{`click`}</inlineCode>{`, or `}<inlineCode parentName="li">{`tap`}</inlineCode>{`: These actions trigger an action or navigate into a submenu, based on the selected item.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`arrow left`}</inlineCode>{`, `}<inlineCode parentName="li">{`arrow up`}</inlineCode>{`, `}<inlineCode parentName="li">{`arrow down`}</inlineCode>{`, `}<inlineCode parentName="li">{`arrow right`}</inlineCode>{`: These actions can be used to navigate the menu, allowing the user to move between items and submenus with ease.`}</li>
    </ul>
    <p>{`Menus are a commonly used feature across multiple platforms, and both `}<a parentName="p" {...{
        "href": "https://developer.apple.com/design/human-interface-guidelines/components/menus-and-actions/menus"
      }}>{`Apple`}</a>{` and `}<a parentName="p" {...{
        "href": "https://learn.microsoft.com/en-us/previous-versions/windows/desktop/bb226797(v=vs.85)"
      }}>{`Microsoft`}</a>{` have detailed documentation on their menus. To create a pleasant user experience, it's important to follow their expected behavior closely.`}</p>
    <h2>{`Anatomy`}</h2>
    <img width="960" alt="Action menu opened with an internal `Settings` label. Several elements are emphasized, such as the trigger, actions, overlay menu, trailing visual shortcuts, trailing visual submenu indicator, and the 3 dot dialog indicator." src="https://user-images.githubusercontent.com/980622/234074196-f67bf01e-29b6-4be1-a6fc-47fc532f536b.png" />
    <h2>{`Content`}</h2>
    <h3>{`Open`}</h3>
    <p>{`Action menus can be opened through a `}<a parentName="p" {...{
        "href": "/components/button"
      }}>{`button`}</a>{`, `}<a parentName="p" {...{
        "href": "/components/icon-button"
      }}>{`icon button`}</a>{` or by right clicking list items that have a custom context menu associated with them.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/18661030/226486473-9cf0c64f-5ae6-40a5-bd0f-87d3ed34f90d.png" />
    <Caption mdxType="Caption">Use a button to open a menu.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/18661030/226486474-08b75358-bfcb-483d-bba0-25947a71c8de.png" />
    <Caption mdxType="Caption">Don’t use a link to open a menu.</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`When using a button the menu can be opened by clicking or tapping it. When it's focused it can also be opened by hitting `}<inlineCode parentName="p">{`return`}</inlineCode>{`, `}<inlineCode parentName="p">{`space`}</inlineCode>{` or any `}<inlineCode parentName="p">{`arrow`}</inlineCode>{` key.`}</p>
    <Box display="grid" gridTemplateColumns={['1fr', null, null, null, '1fr 1fr']} gridGap={5} mdxType="Box">
  <div>
    <img src="https://user-images.githubusercontent.com/18661030/226486479-52730f0f-cc41-41e0-a996-a974899cbc4e.png" alt="" />
    <Caption mdxType="Caption">Open by clicking the trigger button with a mouse</Caption>
  </div>
  <div>
    <img src="https://user-images.githubusercontent.com/18661030/226486480-4a807f86-b2b0-4d12-8a43-8cc95d36d995.png" alt="" />
    <Caption mdxType="Caption">Open by focusing the trigger button and hitting return, space, or any arrow key</Caption>
  </div>
    </Box>
    <h3>{`Close`}</h3>
    <p>{`The menu will close when pressing the `}<inlineCode parentName="p">{`esc`}</inlineCode>{` or `}<inlineCode parentName="p">{`tab`}</inlineCode>{` key or when pressing `}<inlineCode parentName="p">{`enter`}</inlineCode>{` when a item is focused.
Additionally clicking outside the menu item or clicking on a item will close the menu as well.`}</p>
    <Box display="grid" gridTemplateColumns={['1fr', null, null, null, '1fr 1fr']} gridGap={5} mdxType="Box">
  <div>
    <img src="https://user-images.githubusercontent.com/18661030/226486482-fc457572-342d-42d3-9da6-fa87ef009cea.png" alt="" />
    <Caption mdxType="Caption">
      Close the menu by clicking on an item, clicking on the trigger, or clicking anywhere outside the menu.
    </Caption>
  </div>
  <div>
    <img src="https://user-images.githubusercontent.com/18661030/226486483-e3d658ea-4148-47d6-95ed-5ca90cc55b38.png" alt="" />
    <Caption mdxType="Caption">Close the menu by hitting enter on an item, esc or tab.</Caption>
  </div>
    </Box>
    <h4>{`Single and multi select`}</h4>
    <Box display="grid" gridTemplateColumns={['1fr', null, null, null, '1fr 1fr']} gridGap={5} mdxType="Box">
  <div>
    <CustomVideoPlayer width="100%" src="https://user-images.githubusercontent.com/18661030/226487362-b66daa5f-40b3-4f73-908c-19a74cabd435.mp4" mdxType="CustomVideoPlayer" />
    <Caption mdxType="Caption">Single select menu closes upon selection and may update the button label.</Caption>
  </div>
  <div>
    <CustomVideoPlayer width="100%" src="https://user-images.githubusercontent.com/18661030/226487367-680395c7-1859-40a0-bbfc-53f37a686662.mp4" mdxType="CustomVideoPlayer" />
    <Caption mdxType="Caption">Multi select menu closes upon selection and can be re-opened to select more options.</Caption>
  </div>
    </Box>
    <h3>{`Avoid input controls`}</h3>
    <p>{`The focus should always remain on menu items, and therefore the menu shouldn't include additional form elements like `}<inlineCode parentName="p">{`button`}</inlineCode>{`, `}<inlineCode parentName="p">{`input`}</inlineCode>{`, or `}<inlineCode parentName="p">{`checkbox`}</inlineCode>{`.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234083626-6d8f4def-eccd-429b-b815-81071502a2a7.png" />
    <Caption mdxType="Caption">Use action menu for quick actions.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234083619-40ebf8b6-6194-4e66-a7e8-597ee43f9ed8.png" />
    <Caption mdxType="Caption">
      Don't add form controls like a filterable input, use <a href="/components/selectpanel">SelectPanel</a> instead.
    </Caption>
  </Dont>
    </DoDontContainer>
    <p>{`While single and multi selection is offered within a menu, the underlying semantics are not true form elements.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234080409-c2a50287-c215-48e6-96fa-3fb48c636fc7.png" />
    <Caption mdxType="Caption">Use a checkmark icon for selections.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234080401-42a48754-b120-4077-84d1-59cc08f71367.png" />
    <Caption mdxType="Caption">Don't use form elements within a menu.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Alignment`}</h3>
    <p>{`Consistent alignment of icons, titles, and trailing visuals is critical to ensure that the menu is easily scannable. This is especially important when combining single or multi-select items with non-select items.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234072131-9d984962-61d4-4590-94db-43aa7135d7d2.png" />
    <Caption mdxType="Caption">
      When a menu includes a single select or multi select then all the items in the menu should be indented.
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232518089-417184b7-336e-440a-a302-0c1f7c8c4dda.png" />
    <Caption mdxType="Caption">Don't create invidiual alignments when your menu contains a single or multi select.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Icons`}</h3>
    <p>{`When designing menus, it is crucial to adhere to the meaning of the icons used. If any item in the menu does not have a corresponding icon, then all items in that menu should be presented without icons.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234294137-0438d55e-0839-4d6a-9be9-9cd9332c09a8.png" />
    <Caption mdxType="Caption">When one of the items doesn't have an icon then remove icons altogether.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234294131-fcc8558b-4837-4d9a-b1d6-360384ed48b7.png" />
    <Caption mdxType="Caption">Don't mix items with or without icons as this makes it harder to read.</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`Especially when providing single or multi select items that rely on a crossmark icon should be avoided as they could be confused with the item's checkmark.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232508225-59f21afe-1613-4ad0-8088-a8f2f6eb2ba9.png" />
    <Caption mdxType="Caption">Avoid the usage of icons if the icon conflicts with the selected states checkmark.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232508233-78d0460d-c891-42be-929d-ff0465313805.png" />
    <Caption mdxType="Caption">Don't use crossmark icons for single or multi select items.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Dividers`}</h3>
    <p>{`To enhance the readability of menus that contain numerous item descriptions, it is recommended to incorporate dividers. This can effectively prevent the menu from becoming overwhelming.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234297298-76d14987-2ecb-4165-9f5d-ada0fd1c7e17.png" />
    <Caption mdxType="Caption">Introduce dividers for items with heavy descriptions to improve readability.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234297304-0cfdd597-fc79-4394-bb74-b37ec1276bd6.png" />
    <Caption mdxType="Caption">Don't forget to add dividers when the menu content starts to look heavy.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Trailing visuals`}</h3>
    <p>{`Avoid using trailing visuals to add additional interactions, which reserves them for submenu indicators or keyboard shortcuts.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234299628-3c8a8613-7a2f-4f4a-83da-14eab2f28032.png" />
    <Caption mdxType="Caption">Create a new section for additional functionality.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234299623-927d9309-9991-496c-b707-b6f358583928.png" />
    <Caption mdxType="Caption">Don't store additional toggleable functionality in trailing visuals.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Multi-level`}</h3>
    <p>{`A menu with multiple levels is a common UI component used in desktop and mobile applications. It allows users to access nested subitems without cluttering the screen.`}</p>
    <img width="960" alt="Action menu opened with a label for the trigger (button), overlay (menu), and the internal action list." src="https://user-images.githubusercontent.com/980622/232519615-d10c928d-5253-4c54-bc66-7fa1e0f85b08.png" />
    <p>{`It's important to understand that menus can't include additional form elements like inputs. That means if you want to provide a input that filters the subitems that you can't rely on this component. Alternatively, you can use a `}<a parentName="p" {...{
        "href": "/components/selectpanel"
      }}>{`select panel`}</a>{` or open a `}<a parentName="p" {...{
        "href": "/components/dialog"
      }}>{`dialog`}</a>{` instead of a submenu.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232558685-0874d0dd-6d58-42d6-a19b-be19cdf32cf8.png" />
    <Caption mdxType="Caption">If you want to present filterable data open a dialog instead of a submenu.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232547208-aa63f85b-cc9c-4fd2-8222-c26ac993c0cc.png" />
    <Caption mdxType="Caption">Don't use form elements inside a menu.</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`Since you can't include form elements within a menu that also means that you can't combine a `}<a parentName="p" {...{
        "href": "/components/selectpanel"
      }}>{`select panel`}</a>{` with a action menu.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234094256-abf3f1ad-2d90-48f9-84aa-a8e17cc64c47.png" />
    <Caption mdxType="Caption">If you want to present filterable data do this through a dialog.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/234094263-399afdd0-b2e5-45dc-a218-db4a7dcdd9be.png" />
    <Caption mdxType="Caption">Don't mix select panels with action menus.</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`When using submenus, don't use the trailing visual to communicate selected values within its submenu.
Leave this space for submenu indicators.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232562573-a687110d-06cb-4756-8fb3-6b7e05665dd4.png" />
    <Caption mdxType="Caption">Use trailing visuals for submenu indicators or keyboard shortcuts.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/232562567-bb96a0cb-a796-4834-bf91-e3ddff86c312.png" />
    <Caption mdxType="Caption">Don't use trailing visual to communicate selected items in submenus.</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`On smaller viewports, the menu should always be re-positioned to fit within the viewport. This could mean that a submenu overlaps it's parent menu.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/233361999-3b5ab994-152d-455e-b9bc-a9170a1951b1.png" />
    <Caption mdxType="Caption">Reposition menus to stay within the viewport, especially on narrow viewports.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="464" alt="" src="https://user-images.githubusercontent.com/980622/233361993-cf3e0b21-e584-4780-a0e1-42f03fc4d395.png" />
    <Caption mdxType="Caption">Don't position menus off screen, especially on narrow viewports.</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Options`}</h2>
    <h3>{`Trigger`}</h3>
    <img src="https://user-images.githubusercontent.com/980622/234255983-8db23940-e60f-408a-a697-c9846ae4334b.png" width="960" alt="" />
    <Caption mdxType="Caption">
  Menus can be triggered through a button, icon button or right clicking list items. For simple select menus that
  display the selection in a button, a label must be persist either internally or externally.
    </Caption>
    <h3>{`Items`}</h3>
    <img src="https://user-images.githubusercontent.com/980622/234249718-84155711-6b31-4529-bf27-63f99c964957.png" width="960" alt="" />
    <p>{`Here is an overview of `}<a parentName="p" {...{
        "href": "/components/action-menu"
      }}>{`action list`}</a>{` items that are compatible with menus:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Action`}</strong>{`: Triggers an action or dialog. Trailing visuals can be used for keyboard shortcuts.`}</li>
      <li parentName="ul"><strong parentName="li">{`Single/multi select`}</strong>{`: Represented by a `}<a parentName="li" {...{
          "href": "/design/foundations/icons/check-16"
        }}>{`check`}</a>{` and never a HTML checkbox. Trailing visuals can be used for keyboard shortcuts.`}</li>
      <li parentName="ul"><strong parentName="li">{`Submenu`}</strong>{`: Represented by a `}<a parentName="li" {...{
          "href": "/design/foundations/icons/chevron-right-16"
        }}>{`chevron-right`}</a>{` as a trailing visual.`}</li>
      <li parentName="ul"><strong parentName="li">{`Section header`}</strong>{`: To add clarification to a section.`}</li>
      <li parentName="ul"><strong parentName="li">{`Divider`}</strong>{`: To create sections for your selections, actions or submenus.`}</li>
    </ul>
    <h2>{`Accessibility`}</h2>
    <p>{`See `}<a parentName="p" {...{
        "href": "/guides/accessibility/focus-management"
      }}>{`focus management`}</a>{` for more information.`}</p>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 4
    }} mdxType="Box">
  <img width="456" alt="" src="https://user-images.githubusercontent.com/18661030/226486487-b03672c1-c314-4be0-b96b-d4c348b2ff4d.png" />
  <Box as="p" mt="0" mdxType="Box">
    <h4>Menu mnemonics</h4>
    When a menu is open, hitting a letter key for the first letter in an item to be selected will move focus to that item.
    If two items have the same first letter, the first item in the list will be focused first, and hitting the letter key
    again will focus the next item.
  </Box>
    </Box>
    <h3>{`Keyboard navigation`}</h3>
    <h4>{`Trigger button`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Action`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Enter`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Space`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowDown`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowUp`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowLeft`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowRight`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens the menu.`}</td>
        </tr>
      </tbody>
    </table>
    <h4>{`Menu items`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Action`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowUp`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Cycle through items starting with current item to previous item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowDown`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Cycle through items starting with current item to next item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowLeft`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Closes submenu and sets focus back to the parent item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowRight`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opens a submenu`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Home`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Move focus to first item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`PageUp`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Move focus to first item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`End`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Move focus to last item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`PageDown`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Move focus to last item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Esc`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Close menu`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Related components`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/action-list"
        }}>{`Action list`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/button"
        }}>{`Button`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/icon-button"
        }}>{`Icon button`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/selectpanel"
        }}>{`Select panel`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      